'use client'

import { Box, Text, Flex, HStack, Button, VStack } from '@chakra-ui/react'
import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useTranslations } from 'next-intl'
import * as echarts from 'echarts/core';
import { MarkPoint, useChartMarkPoints } from '@/lib/hooks/useChartMarkPoints';
import { usePortfolioData } from '@/lib/hooks/usePortfolioData';
import { useRouter } from 'next/navigation'
import { Portfolio } from '@/lib/utils/hyper/types';
import { formatTime } from '@/lib/utils/day.utils'
import { humanFormatNumber } from '@/lib/utils/format';
import { LoadingState } from '../common/LoadingState';
import { MAX_WIDTH_IN_WINDOW } from '@/lib/config/page';
import DynamicReactECharts from '../charts/DynamicReactECharts';

interface PortfolioChartProps {
  userAddress: string
}

export type ChartType = 'totalReturn' | 'accountAssets'
export type TimePeriod = 'perpDay' | 'perpWeek' | 'perpMonth' | 'perpAllTime'

const timePeriods: Array<{ key: TimePeriod, translationKey: string }> = [
  { key: 'perpDay', translationKey: 'daily' },
  { key: 'perpWeek', translationKey: 'weekly' },
  { key: 'perpMonth', translationKey: 'monthly' },
  { key: 'perpAllTime', translationKey: 'all' },
]

const chartTypes: Array<{ key: ChartType, translationKey: string }> = [
  { key: 'accountAssets', translationKey: 'accountAssets' },
  { key: 'totalReturn', translationKey: 'totalReturn' },
]

export function PortfolioChart({ userAddress }: PortfolioChartProps) {
  const t = useTranslations()
  const chartRef = useRef<HTMLElement>(null)

  const [loadingChart, setLoadingChart] = useState(true)
  const [activeChart, setActiveChart] = useState<ChartType>('accountAssets')
  const [activePeriod, setActivePeriod] = useState<TimePeriod>('perpDay')
  const [storiesModalVisible, setStoriesModalVisible] = useState(false)
  const [selectedMarkPoints, setSelectedMarkPoints] = useState<MarkPoint[]>([])
  const loadingTimer = useRef<NodeJS.Timeout | null>(null)

  const router = useRouter()

  const screenWidth = Math.min(window.innerWidth, MAX_WIDTH_IN_WINDOW)
  const chartWidth = screenWidth - 16 // Account for padding
  const chartHeight = 258
  
    // Fetch portfolio data from Hyperliquid API
  const { data: portfolioPeriods, isLoading, error } = usePortfolioData(userAddress)
  
  // Extract portfolio data for the selected period
  const portfolioData = useMemo(() => {
    if (!portfolioPeriods) {
      return { accountValueHistory: [], pnlHistory: [], vlm: '0' }
    }

    // Find the data for the selected period
    const periodData = portfolioPeriods.find(([period]: [string, Portfolio]) => period === activePeriod)
    return periodData?.[1] || { accountValueHistory: [], pnlHistory: [], vlm: '0' }
  }, [portfolioPeriods, activePeriod])

  // Prepare chart data based on active chart type
  const chartData = useMemo(() => {
    const historyData
      = activeChart === 'accountAssets' ? portfolioData.accountValueHistory : portfolioData.pnlHistory

    if (!historyData || historyData.length === 0) {
      return { timestamps: [], values: [], formattedTimes: [] }
    }

    const timestamps = historyData.map(([timestamp]: [number, string]) => timestamp)
    const values = historyData.map(([, value]: [number, string]) => Number.parseFloat(value))
    const formattedTimes = timestamps.map((ts: number) => formatTime(ts))

    return { timestamps, values, formattedTimes }
  }, [portfolioData, activeChart])


  // Fetch mark points using the new hook
  const { data: markPointsData } = useChartMarkPoints({
    timestamps: chartData.timestamps,
    values: chartData.values,
    activePeriod,
    activeChart,
    enabled: chartData.values.length > 0,
  })

  // Helper function to calculate timestamp difference in minutes
  const calculateTimestampDistance = (timestamp1: number, timestamp2: number): number => {
    return Math.abs(timestamp1 - timestamp2) / (1000 * 60) // Convert to minutes
  }

  // Calculate dynamic threshold based on chart data time range
  const calculateDynamicThreshold = (timestampsList: number[]): number => {
    if (timestampsList.length < 2)
      return 30 // Default fallback

    const timeRange = timestampsList.at(-1)! - timestampsList[0]
    const timeRangeMinutes = timeRange / (1000 * 60) // Convert to minutes

    // Use 12% of the total time range as proximity threshold
    const dynamicThreshold = timeRangeMinutes * 0.12

    return dynamicThreshold
  }

  // Helper function to find nearby mark points based on timestamp proximity
  const findNearbyMarkPoints = useCallback(
    (clickedTimestamp: number): MarkPoint[] => {
      if (!markPointsData)
        return []

      const threshold = calculateDynamicThreshold(chartData.timestamps)
      const nearbyPoints = markPointsData.filter((point) => {
        const timeDifference = calculateTimestampDistance(clickedTimestamp, point.timestamp)

        return timeDifference <= threshold
      })

      return nearbyPoints
    },
    [markPointsData, chartData.timestamps],
  )
  const chartHandler = useCallback(
    (params: any) => {
      if (params.componentType === 'markPoint') {
        // Get the clicked mark point timestamp
        const clickedTimestamp = params.data.timestamp as number

        // Find nearby mark points using dynamic threshold based on chart time range
        const nearbyPoints = findNearbyMarkPoints(clickedTimestamp)

        if (nearbyPoints.length > 1) {
          // Multiple nearby points found - open modal to display them
          setSelectedMarkPoints(nearbyPoints)
          setStoriesModalVisible(true)
        }
        else {
          // Single point - navigate directly to story detail
          // router.push(`/trader/${userAddress}/story/${'test'}`)
        }
      }
    },
    [findNearbyMarkPoints, router, userAddress],
  )
  // ECharts options
  const chartOptions = useMemo(() => {
    if (chartData.values.length === 0 || markPointsData === undefined) {
      return {}
    }

    const isPositive = chartData.values.at(-1) ?? (chartData.values[0] || 0) <= 0
    const lineColor = isPositive ? '#179B90' : '#E34376'
    const maxValue = Math.max(...chartData.values)
    const maxColor = maxValue >= 0 ? '#179B90' : '#E34376'
    const minValue = Math.min(...chartData.values)
    const minColor = minValue >= 0 ? '#179B90' : '#E34376'

    return {
      backgroundColor: 'transparent',
      grid: {
        left: '3%',
        right: '10%',
        top: '10%',
        bottom: '15%',
        containLabel: true,
      },
      // tooltip: {
      //   trigger: 'axis',
      //   backgroundColor: 'rgba(43, 50, 70, 0.9)',
      //   borderColor: 'transparent',
      //   textStyle: {
      //     color: '#ffffff',
      //     fontSize: 11,
      //   },
      //   formatter: (params: any[]) => {
      //     if (params && params[0]) {
      //       const dataIndex = params[0].dataIndex
      //       const value = chartData.values[dataIndex]
      //       const time = chartData.formattedTimes[dataIndex]
      //       return `${time}<br/>$${value.toLocaleString()}`
      //     }
      //     return ''
      //   },
      // },
      xAxis: {
        type: 'category',
        data: chartData.timestamps.map(timestamp => `${timestamp}`),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: 'rgba(255, 255, 255, 0.4)',
          fontSize: 10,
          fontWeight: 'medium',
          padding: 15,
          interval: function(index: number) {
            const n = Math.floor(chartData.timestamps.length / 3);
            return index % n === 0 || index === chartData.timestamps.length - 1;
          },
          formatter: (value: string) => {
            return formatTime(Number(value), activePeriod === 'perpDay')
          },
        },
      },
      yAxis: {
        type: 'value',
        position: 'right',
        scale: true,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: 'rgba(255, 255, 255, 0.4)',
          fontSize: 10,
          padding: 15,
          fontWeight: 'medium',
        },
        splitLine: { show: false },
      },
      series: [
        {
          data: chartData.values,
          type: 'line',
          smooth: false,
          symbol: 'none',
          lineStyle: {
            color: lineColor,
            width: 1.5,
          },
          itemStyle: {
            color: lineColor,
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: `${lineColor}20` },
                { offset: 1, color: 'transparent' },
              ],
            },
          },
          markPoint: {
            data: [
              // Highest value point
              {
                type: 'max',
                name: 'Highest',
                symbol: 'circle',
                symbolSize: 6,
                itemStyle: {
                  color: '#fff',
                  borderColor: maxColor,
                  borderWidth: 2,
                },
                label: {
                  show: true,
                  position: 'top',
                  formatter: (item: any) => {
                    return humanFormatNumber(item.value, 2, false, '$')
                  },
                  fontSize: 12,
                  color: maxColor,
                  backgroundColor: '#000',
                  padding: 0,
                  offset: [0, 0],
                },
              },
              // Lowest value point
              {
                type: 'min',
                name: 'Lowest',
                symbol: 'circle',
                symbolSize: 6,
                itemStyle: {
                  color: '#fff',
                  borderColor: minColor,
                  borderWidth: 2,
                },
                label: {
                  show: true,
                  position: 'bottom',
                  formatter: (item: any) => {
                    return humanFormatNumber(item.value, 2, false, '$')
                  },
                  fontSize: 12,
                  color: minColor,
                  backgroundColor: '#000',
                  padding: 0,
                  offset: [0, 0],
                },
              },
              // ...markPointsData?.map((point) => ({
              //   name: point.name,
              //   coord: point.coord,
              //   symbol: `image://${point.tokenIcon}`,
              //   symbolSize: point.symbolSize,
              //   itemStyle: {
              //     borderColor: point.itemStyle.borderColor,
              //     borderWidth: point.itemStyle.borderWidth,
              //   },
              //   timestamp: point.timestamp,
              //   label: point.label,
              // })),
            ],
          },
        },
      ],
    }
  }, [chartData, markPointsData, activePeriod])

  // Show loading state
  if (isLoading) {
    return (
      <Box p={4} borderBottom="1px solid" borderColor="bg.t0/10">
        <LoadingState />
      </Box>
    )
  }

  // Show error state
  if (error) {
    return (
      <Box  p={4} borderBottom="1px solid" borderColor="bg.t0/10">
        <HStack flex={1} align="center" justify="center" style={{ height: chartHeight }}>
          <Text fontSize="sm" color="red.400">{t('trader.chart.error')}</Text>
        </HStack>
      </Box>
    )
  }

  return (
    <Box px={4} py={2} border=" 1px solid rgba(255, 255, 255, 0.15)" rounded="16px">
      {/* Header with chart type selector */}
      <HStack mb={2} gap={4} align="center">
        {chartTypes.map(type => (
          <Button
            key={type.key}
            variant="ghost"
            size="sm"
            p={2}
            onClick={() => setActiveChart(type.key)}
            color={activeChart === type.key ? 'text.t1/90' : 'text.t1/40'}
            fontWeight={activeChart === type.key ? 'bold' : 'medium'}
            _hover={{ bg: 'transparent' }}
          >
            {t(`trader.chart.${type.translationKey}`)}
          </Button>
        ))}
      </HStack>

      {/* Time period selector */}
      <HStack justify="space-between" mb={4} px={2}>
        {timePeriods.map(period => (
          <Button
            key={period.key}
            size="xs"
            variant={activePeriod === period.key ? 'solid' : 'ghost'}
            onClick={() => setActivePeriod(period.key)}
            bg={activePeriod === period.key ? 'bg.t0/10' : 'transparent'}
            color={activePeriod === period.key ? 'text.t1/90' : 'text.t1/40'}
            _hover={{ bg: activePeriod === period.key ? 'bg.t0/10' : 'bg.t0/50' }}
            px={3}
            py={2}
            borderRadius="full"
          >
            {t(`trader.chart.${period.translationKey}`)}
          </Button>
        ))}
      </HStack>

      {/* Chart area */}
      <Box rounded="lg" bg="transparent" style={{ height: chartHeight }}>
        {chartData.values.length > 0 ? (
          <DynamicReactECharts
            echarts={echarts}
            option={chartOptions}
            notMerge={true}
            lazyUpdate={true}
            theme={"dark"}
            opts={{
              renderer: 'canvas',
            }}
            style={{
              width: '100%',
              height: chartHeight,
            }}
            onChartReady={() => setLoadingChart(false)}
            onEvents={{
              'click': chartHandler
            }}
          />
        ) : (
          <HStack flex={1} justify="center" align="center">
            <Text fontSize="sm" color="text.t1/60">{t('trader.chart.noData')}</Text>
          </HStack>
        )}
        {loadingChart && (
          <Box position='absolute' inset={0} bg="bg.t0/5" />
        )}
      </Box>

      {/* Trading Stories Modal */}
      {/* <TradingStoriesModal
        visible={storiesModalVisible}
        onClose={() => {
          setStoriesModalVisible(false)
        }}
        userAddress={userAddress}
        markPoints={selectedMarkPoints}
      /> */}
    </Box>
  )
}